import React, { useEffect, useState } from 'react';
import DashboardLayout from '../components/Nav';
import { BadgeCheck, Clock, Cpu, Key, UserCheck, ShieldAlert, Info, RefreshCcw } from 'lucide-react';
import getClientConfig from '../config';

const { API_BASE_URL } = getClientConfig();

export default function LicenseStatusPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('auth_token');

  useEffect(() => {
    document.title = "License Status | IP Intelligence Portal";
  }, []);

  const fetchStatus = () => {
    setLoading(true);
    fetch(`${API_BASE_URL}/license-status`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => {
        setStatus(data);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto p-8 bg-white shadow-2xl rounded-3xl border border-gray-200 animate-fade-in">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-3xl font-black text-blue-700 flex items-center gap-3">
            <BadgeCheck className="w-7 h-7" /> License Status
          </h2>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchStatus}
              title="Refresh"
              className="rounded-full p-2 bg-gray-100 hover:bg-gray-200 transition"
            >
              <RefreshCcw className="w-4 h-4 text-gray-600" />
            </button>
            <span className={`inline-flex items-center text-xs font-medium px-3 py-1 rounded-full ${status?.valid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}> 
              {status?.valid ? 'ACTIVE' : 'VIOLATION'}
            </span>
          </div>
        </div>

        {loading ? (
          <div className="text-center text-gray-500 animate-pulse mt-10">
            <Clock className="mx-auto mb-3 w-6 h-6 text-blue-500" />
            Fetching license status...
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 text-sm">
            <div className="p-6 bg-gradient-to-tr from-blue-100 via-white to-blue-50 border border-blue-200 rounded-xl shadow-md">
              <p className="text-gray-500 font-medium mb-1 flex items-center gap-2"><ShieldAlert className="w-4 h-4" /> License Status</p>
              <p className={`text-xl font-bold ${status.valid ? 'text-green-600' : 'text-red-600'}`}> 
                {status.valid ? '✅ Valid & Active' : '❌ Invalid / Overused'}
              </p>
            </div>

            <div className="p-6 bg-gradient-to-tr from-indigo-100 via-white to-indigo-50 border border-indigo-200 rounded-xl shadow-md">
              <p className="text-gray-500 font-medium mb-1 flex items-center gap-2"><Cpu className="w-4 h-4" /> NAS Usage</p>
              <p className={`text-xl font-bold ${status.used > status.allowed ? 'text-red-500' : 'text-gray-800'}`}>
                {status.used} / {status.allowed}
              </p>
            </div>

            <div className="p-6 bg-gradient-to-tr from-green-100 via-white to-green-50 border border-green-200 rounded-xl shadow-md">
              <p className="text-gray-500 font-medium mb-1 flex items-center gap-2"><UserCheck className="w-4 h-4" /> License Type</p>
              <p className="text-base font-semibold text-green-700">{status.type || 'N/A'}</p>
            </div>

            <div className="p-6 bg-gradient-to-tr from-yellow-100 via-white to-yellow-50 border border-yellow-200 rounded-xl shadow-md col-span-2">
              <p className="text-gray-500 font-medium mb-1 flex items-center gap-2"><Key className="w-4 h-4" /> System ID</p>
              <p className="text-xs text-gray-700 font-mono break-words max-w-full whitespace-pre-wrap">{status.system_id || 'N/A'}</p>
            </div>

            <div className="p-6 bg-gradient-to-tr from-purple-100 via-white to-purple-50 border border-purple-200 rounded-xl shadow-md col-span-2">
              <p className="text-gray-500 font-medium mb-1 flex items-center gap-2"><Key className="w-4 h-4" /> License Key</p>
              <div className="text-xs text-gray-700 font-mono bg-gray-100 p-3 rounded-lg overflow-auto break-words">
                {status.license_id}
              </div>
            </div>

            <div className="p-6 bg-gradient-to-tr from-red-100 via-white to-red-50 border border-red-200 rounded-xl shadow-md">
              <p className="text-gray-500 font-medium mb-1 flex items-center gap-2"><Clock className="w-4 h-4" /> Expiry Date</p>
              <p className="text-sm font-semibold text-gray-800">
                {status.expiry_date ? new Date(status.expiry_date).toLocaleString() : 'N/A'}
              </p>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}